import { useState, useEffect } from "react";
import GameStatsTables from "../GameStatsTables";

export default function AddPlayerStatsForm({ API, accessToken, selectedTeamId, getTeamName }) {
  const [players, setPlayers] = useState([]);
  const [teamGames, setTeamGames] = useState([]);
  const [stats, setStats] = useState([]);
  const [groupedStats, setGroupedStats] = useState({});
  const [formStat, setFormStat] = useState({});
  const [editing, setEditing] = useState(false);
  const [timeInput, setTimeInput] = useState(""); // MM:SS for minutes

  // Fetch players
  useEffect(() => {
    if (!selectedTeamId) return;

    const fetchPlayers = async () => {
      try {
        const res = await fetch(`${API}/players`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const data = await res.json();
        setPlayers(Array.isArray(data) ? data.filter(p => Number(p.team_id) === Number(selectedTeamId)) : []);
      } catch (err) {
        console.error("Failed to fetch players:", err);
      }
    };

    fetchPlayers();
  }, [API, accessToken, selectedTeamId]);

  // Fetch games for this team
  useEffect(() => {
    if (!selectedTeamId) return;

    const fetchGames = async () => {
      try {
        const res = await fetch(`${API}/games`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const data = await res.json();
        setTeamGames(
          data
            .filter(
              g => Number(g.home_team_id) === Number(selectedTeamId) || Number(g.away_team_id) === Number(selectedTeamId)
            )
            .sort((a, b) => new Date(a.date) - new Date(b.date))
        );
      } catch (err) {
        console.error("Failed to fetch games:", err);
      }
    };

    fetchGames();
  }, [API, accessToken, selectedTeamId]);

  // Fetch stats
  useEffect(() => {
    if (!selectedTeamId) return;

    const fetchStats = async () => {
      try {
        const res = await fetch(`${API}/teams/${selectedTeamId}/player_stats`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const data = await res.json();

        const teamStats = data.filter(s => Number(s.team_id) === Number(selectedTeamId));
        setStats(teamStats);

        const grouped = {};
        teamStats.forEach(s => {
          if (!grouped[s.game_id]) grouped[s.game_id] = [];
          grouped[s.game_id].push(s);
        });
        setGroupedStats(grouped);
      } catch (err) {
        console.error("Failed to fetch stats:", err);
      }
    };

    fetchStats();
  }, [API, accessToken, selectedTeamId]);

  // Submit stat (add or edit)
  const handleSubmitStat = async (e) => {
    e.preventDefault();
    if (!formStat.player_id || !formStat.game_id) return;

    // Convert MM:SS → seconds
    let minutesInSeconds = null;
    if (timeInput) {
      const [m, s] = timeInput.split(":").map(Number);
      if (!isNaN(m) && !isNaN(s)) {
        minutesInSeconds = m * 60 + s;
      }
    }

    const payload = { ...formStat, minutes: minutesInSeconds, team_id: selectedTeamId };

    try {
      const res = await fetch(`${API}/stats`, {
        method: "POST", // use PUT for editing
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Failed to submit stat");

      // Refresh stats
      const updated = await fetch(`${API}/teams/${selectedTeamId}/player_stats`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      }).then(r => r.json());

      const teamStats = updated.filter(s => Number(s.team_id) === Number(selectedTeamId));
      setStats(teamStats);

      const grouped = {};
      teamStats.forEach(s => {
        if (!grouped[s.game_id]) grouped[s.game_id] = [];
        grouped[s.game_id].push(s);
      });
      setGroupedStats(grouped);

      setFormStat({});
      setTimeInput("");
      setEditing(false);
    } catch (err) {
      console.error(err);
      alert("Error submitting stat");
    }
  };

  // Delete player's stat row
  const removePlayerFromGame = async (statId) => {
    if (!window.confirm("Are you sure you want to remove this player from the game?")) return;

    try {
      await fetch(`${API}/stats/${statId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      // Update local state
      setStats((prev) => prev.filter((s) => s.id !== statId));
      setGroupedStats((prev) => {
        const updated = { ...prev };
        for (const gameId in updated) {
          updated[gameId] = updated[gameId].filter((s) => s.id !== statId);
          if (updated[gameId].length === 0) delete updated[gameId];
        }
        return updated;
      });
    } catch (err) {
      console.error(err);
      alert("Failed to remove player from game");
    }
  };

  // Start editing a stat
  const startEditing = (stat) => {
    setFormStat(stat);
    setEditing(true);

    // Convert seconds → MM:SS
    if (stat.minutes !== null && stat.minutes !== undefined) {
      const m = Math.floor(stat.minutes / 60).toString().padStart(2, "0");
      const s = (stat.minutes % 60).toString().padStart(2, "0");
      setTimeInput(`${m}:${s}`);
    } else {
      setTimeInput("");
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="p-4">
      {/* Add/Edit Stats */}
      <div className="max-w-lg mx-auto mb-10">
        <h2 className="text-xl font-bold mb-4 text-center">
          {formStat.id ? "Edit Player Stat" : "Add Player Stat"}
        </h2>

        <form onSubmit={handleSubmitStat} className="flex flex-col gap-3 bg-gray-100 p-4 rounded-lg shadow-md">
          {/* Select Player */}
          <select
            value={formStat.player_id || ""}
            onChange={(e) => setFormStat({ ...formStat, player_id: Number(e.target.value) })}
            className="w-full border p-2 rounded"
            required
          >
            <option value="">Select Player</option>
            {players.map((p) => (
              <option key={p.id} value={p.id}>{p.player_name}</option>
            ))}
          </select>

          {/* Select Game */}
          <select
            value={formStat.game_id || ""}
            onChange={(e) => setFormStat({ ...formStat, game_id: Number(e.target.value) })}
            className="w-full border p-2 rounded"
            required
          >
            <option value="">Select Game</option>
            {teamGames.length > 0 ? (
              teamGames.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.home_team ?? getTeamName(g.home_team_id)} vs {g.away_team ?? getTeamName(g.away_team_id)} — {g.date}
                </option>
              ))
            ) : (
              <option value="" disabled>No games available</option>
            )}
          </select>

          {/* Stat Fields */}
          {[
            "two_fg_attempts","two_fg_made","three_fg_attempts","three_fg_made",
            "ft_attempts","ft_made","off_rebounds","def_rebounds","assists",
            "steals","blocks","turnovers","fouls","bpm"
          ].map((field) => (
            <div key={field} className="flex items-center gap-3">
              <label className="w-48 text-sm font-medium capitalize">
                {field.replace(/_/g, " ")}
              </label>
              <input
                type="number"
                value={formStat[field] ?? ""}
                onChange={(e) =>
                  setFormStat({
                    ...formStat,
                    [field]: e.target.value === "" ? "" : Number(e.target.value),
                  })
                }
                className="flex-1 border p-2 rounded appearance-none"
                inputMode="numeric"
              />
            </div>
          ))}

          {/* Minutes Field */}
          <div className="flex items-center gap-3">
            <label className="w-48 text-sm font-medium capitalize">Minutes (MM:SS)</label>
            <input
              type="text"
              value={timeInput}
              onChange={(e) => setTimeInput(e.target.value)}
              placeholder="MM:SS"
              className="flex-1 border p-2 rounded"
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-2 mt-2">
            <button type="submit" className="bg-green-500 text-black px-4 py-2 rounded hover:bg-green-600">
              Save Changes
            </button>

            {editing && formStat.id && (
              <button
                type="button"
                className="bg-red-500 text-black px-4 py-2 rounded hover:bg-red-600"
                onClick={() => removePlayerFromGame(formStat.id)}
              >
                Delete
              </button>
            )}

            <button
              type="button"
              className="bg-gray-300 text-black px-4 py-2 rounded hover:bg-gray-400"
              onClick={() => {
                setFormStat({});
                setTimeInput("");
                setEditing(false);
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      {/* Existing Stats */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Existing Stats</h3>
        {Object.entries(groupedStats).length ? (
          Object.entries(groupedStats).map(([gameId, gameStats]) => {
            const game = teamGames.find((g) => Number(g.id) === Number(gameId));
            if (!game) return null;
            return (
              <div key={gameId} className="mb-6 border-b pb-4">
                <GameStatsTables
                  stats={gameStats}
                  games={teamGames}
                  teamId={selectedTeamId} 
                  showEditButtons={true}
                  onEdit={startEditing}
                />
              </div>
            );
          })
        ) : (
          <p>No stats for this team yet.</p>
        )}
      </div>
    </div>
  );
}
